import { Segment, User } from '../types'

export const normalizeSegments = (
  segmentsToNormalize: Segment[],
): Segment[] => {
  if (segmentsToNormalize.length < 2) {
    return segmentsToNormalize
  }

  const normalized: Segment[] = []

  for (const segment of segmentsToNormalize) {
    const lastSegment = normalized[normalized.length - 1]

    // Check for mergeable condition:
    // - `lastSegment` exists
    // - `lastSegment` is a 'text' segment
    // - `segment` is a 'text' segment
    // - Neither are special newline barriers
    if (
      lastSegment &&
      lastSegment.type === 'text' &&
      segment.type === 'text' &&
      lastSegment.content !== '\n' &&
      segment.content !== '\n'
    ) {
      // Merge the current segment's content into the last one.
      // The ID of the last segment is preserved.
      lastSegment.content += segment.content
    } else {
      // If segments can't be merged, push a copy of the current segment.
      // We push a copy to avoid mutating the original array passed to the function.
      normalized.push({ ...segment })
    }
  }

  return normalized
}

export const parseStringToSegments = (
  text: string,
  nextId: React.MutableRefObject<number>,
  users: User[],
): Segment[] => {
  if (!text) return []

  const newSegments: Segment[] = []
  // Regex to find potential mentions. It looks for @ followed by words, allowing spaces in between.
  // The 'u' flag is for unicode property escapes \p{L} (any letter).
  const mentionRegex = /@([\p{L}\p{N}_]+(?:\s[\p{L}\p{N}_]+)*)/gu
  let lastIndex = 0
  let match

  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionName = match[1]
    const fullMention = match[0]
    const startIndex = match.index

    // Check if the found name matches a user
    const user = users.find(
      u => u.name
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase() ===
      mentionName
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase(),
    )

    // Add the text segment before the mention
    if (startIndex > lastIndex) {
      newSegments.push({
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: text.substring(lastIndex, startIndex),
      })
    }

    if (user) {
      // It's a valid user, create a finalized mention segment
      newSegments.push({
        id: `seg-${nextId.current++}`,
        type: 'mention',
        content: fullMention,
        isModified: false, // All parsed mentions are considered final
      })
    } else {
      // It's not a user, treat it as plain text
      newSegments.push({
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: fullMention,
      })
    }

    lastIndex = startIndex + fullMention.length
  }

  // Add any remaining text after the last mention
  if (lastIndex < text.length) {
    newSegments.push({
      id: `seg-${nextId.current++}`,
      type: 'text',
      content: text.substring(lastIndex),
    })
  }

  // A final normalization pass to merge any adjacent text segments
  return normalizeSegments(newSegments)
} 